import { BannerProps, BannerStatus } from '../banner.type';
import styles from './banner-notification.module.scss';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Banner, Button, BUTTON_VARIANT, Icon, Typography } from '../../../components';
import { GHubTheme, useGetTheme } from '../../theme';
import clsx from 'clsx';

type BannerNotificationProps = {
  banner: BannerProps;
  closeBanner: () => void;
};

const mapBannerStatus = (toastVariant: BannerStatus) => {
  switch (toastVariant) {
    case 'alert':
      return Banner.VARIANT.ALERT;
    case 'info':
      return Banner.VARIANT.INFO;
    case 'error':
      return Banner.VARIANT.ERROR;
    default:
      return Banner.VARIANT.ALERT;
  }
};

const mapBannerIcon = (toastVariant: BannerStatus) => {
  switch (toastVariant) {
    case 'alert':
      return <Icon icon='alert_warning' />;
    case 'info':
      return <Icon icon='action_info' />;
    case 'error':
      return <Icon icon='alert_error' />;
    default:
      return <Icon icon='alert_error' />;
  }
};

const mapBannerButtonColour = (toastVariant: BannerStatus) => {
  switch (toastVariant) {
    case 'alert':
      return '';
    case 'info':
      return styles.infoBannerButton;
    case 'error':
      return styles.errorBannerButton;
    default:
      return '';
  }
};

export const BannerNotification = ({ closeBanner, banner }: BannerNotificationProps) => {
  const { pathname } = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const theme = useGetTheme();

  useEffect(() => {
    if (banner.boundRoute && banner.boundRoute !== pathname) closeBanner();
  }, [pathname, banner.boundRoute, closeBanner]);

  return (
    <Banner
      variant={mapBannerStatus(banner.status)}
      highlight={false}
      icon={mapBannerIcon(mapBannerStatus(banner.status))}
      primaryButton={
        banner.options?.button ? (
          <GHubTheme theme={theme} variant='secondary'>
            <Button
              className={styles.bannerButton}
              variant={BUTTON_VARIANT.SECONDARY}
              loading={isLoading}
              disabled={isLoading}
              onClick={async () => {
                setIsLoading(true);
                await banner.options?.button?.onClick();
                setIsLoading(false);
              }}
            >
              {banner.options?.button?.title}
            </Button>
          </GHubTheme>
        ) : undefined
      }
      secondaryButton={
        banner.options?.disableDismiss ? undefined : (
          <GHubTheme theme={theme} variant='secondary'>
            <Button
              className={clsx(styles.bannerButton, mapBannerButtonColour(banner.status))}
              variant={BUTTON_VARIANT.TERTIARY}
              disabled={isLoading}
              onClick={() => {
                closeBanner();
              }}
            >
              Dismiss
            </Button>
          </GHubTheme>
        )
      }
      title={banner.title}
    >
      <Typography variant={Typography.VARIANT.B3} color={Typography.COLOR.TERTIARY}>
        {banner.message}
      </Typography>
    </Banner>
  );
};
